import React, { useEffect } from 'react'

// Components

import TextInput from '../../../UI/FormInput/TextInput';
import DropDownInput from '../../../UI/FormInput/DropDownInput';
import MaskInput from '../../../UI/FormInput/MaskInput';
import CalendarInput from '../../../UI/FormInput/CalendarInput';
import FormInputGroup from '../../../UI/FormInputGroup/FormInputGroup';

// Hooks

import GetStatesByCountry from '../../../../hooks/GetStatesByCountry';
import GetCitiesByState from '../../../../hooks/GetCitiesByState';
import GetMunicipalitiesByState from '../../../../hooks/GetMunicipalitiesByState';
import GetParishesByMunicipality from '../../../../hooks/GetParishesByMunicipality';
import GetSectorsByState from '../../../../hooks/GetSectorsByState';

// Utilities

import { formatDropDownOptions, getCurrentDateMinusYears } from '../../../../utilities/helper-functions';

// Constants

import { IDENTIFICATION_MASK_FORMAT, PASSPORT_MASK_FORMAT } from '../../../../config/constants';

export default function RepresentativeFields({ formik, dropDownOptions }) {
    const { getStatesIsLoading, getStatesError, states, handleGetStatesByCountry } = GetStatesByCountry();
    const { getCitiesIsLoading, getCitiesError, cities, handleGetCitiesByState } = GetCitiesByState();
    const { getMunicipalitiesIsLoading, getMunicipalitiesError, municipalities, handleGetMunicipalitiesByState } = GetMunicipalitiesByState();
    const { getParishesIsLoading, getParishesError, parishes, handleGetParishesByMunicipality } = GetParishesByMunicipality();
    const { getSectorsIsLoading, getSectorsError, sectors, handleGetSectorsByState } = GetSectorsByState();


    // Get the States by Country
    
    useEffect(() => {
        if (formik.values?.apoderado?.pais === '0') {
            handleGetStatesByCountry(formik.values?.apoderado?.pais);
        }
        if (formik.values?.apoderado?.pais) {
            const code = formik.values?.apoderado?.pais;
            const item = dropDownOptions?.data?.countries?.find((item) => item.value === code);
            formik.setFieldValue(`apoderado.nombrePais`, item?.name);
        }
    }, [handleGetStatesByCountry, formik.values?.apoderado?.pais])


    // Get the Cities by State

    useEffect(() => {
        if (formik.values?.apoderado?.entidad) {
            handleGetCitiesByState(formik.values?.apoderado?.entidad);
        }
    }, [handleGetCitiesByState, formik.values?.apoderado?.entidad])


    // Get the Municipality by State

    useEffect(() => {
        if (formik.values?.apoderado?.entidad) {
            handleGetMunicipalitiesByState(formik.values?.apoderado?.entidad);
        }
    }, [handleGetMunicipalitiesByState, formik.values?.apoderado?.entidad])


    // Get the Parishes by Municipality 

    useEffect(() => {
        if (formik.values?.apoderado?.municipio) {
            handleGetParishesByMunicipality(formik.values?.apoderado?.municipio);
        }
    }, [handleGetParishesByMunicipality, formik.values?.apoderado?.municipio])


    // Get the Sectors by State

    useEffect(() => {
        if (formik.values?.apoderado?.entidad) {
            handleGetSectorsByState(formik.values?.apoderado?.entidad);
        }
    }, [handleGetSectorsByState, formik.values?.apoderado?.entidad])

    return (
        <FormInputGroup>

            <TextInput
                formik={formik}
                fieldName={'apoderado.nombre'}
                label={'Nombres'}
                maxLength={42}
                isRequired={true}
                isAlphabetic
            />

            <TextInput
                formik={formik}
                fieldName={'apoderado.primerApellido'}
                label={'Primer Apellido'}
                maxLength={20}
                isRequired={true}
                isAlphabetic
            />

            <TextInput
                formik={formik}
                fieldName={'apoderado.segundoApellido'}
                label={'Segundo Apellido'}
                maxLength={20}
                isAlphabetic
            />

            <CalendarInput
                formik={formik}
                fieldName={'apoderado.fechaNacimiento'}
                label={'Fecha de Nacimiento'}
                minDate={getCurrentDateMinusYears(90)}
                maxDate={getCurrentDateMinusYears(18)}
                isRequired={true}
            />

            <DropDownInput
                formik={formik}
                fieldName={'apoderado.tipoDocumento'}
                label={'Tipo de Identificación'}
                options={formatDropDownOptions(dropDownOptions?.data?.identificationTypes)}
                isRequired={true}
            />

            {(formik.values.apoderado?.tipoDocumento && formik.values.apoderado?.tipoDocumento !== 'P') &&
                <MaskInput
                    formik={formik}
                    fieldName={'apoderado.cedulaRif'}
                    label={'Número de Identificación'}
                    mask={IDENTIFICATION_MASK_FORMAT}
                    isRequired={true}
                />}

            {formik.values.apoderado?.tipoDocumento === 'P' &&
                <MaskInput
                    formik={formik}
                    fieldName={'apoderado.pasaporte'}
                    label={'Número de Pasaporte'}
                    mask={PASSPORT_MASK_FORMAT}
                    isRequired={true}
                />}

            {formik.values.apoderado?.tipoDocumento &&
                <CalendarInput
                    formik={formik}
                    fieldName={'apoderado.fechaVenceIdentificacion'}
                    label={'Fecha Vencimiento Identificación'}
                    minDate={formik.values.apoderado?.tipoDocumento == 'P' ? new Date() : null}
                    isRequired={true}
                />}

            <DropDownInput
                formik={formik}
                fieldName={'apoderado.pais'}
                label={'Pais'}
                options={formatDropDownOptions(dropDownOptions?.data?.countries)}
                isRequired={true}
            />

            {(formik.values?.apoderado?.pais === '' || formik.values?.apoderado?.pais === '0') &&
                <>

                    <DropDownInput
                        formik={formik}
                        fieldName={'apoderado.entidad'}
                        label={'Estado'}
                        options={formatDropDownOptions(states)}
                        isRequired={true}
                        disabled={states?.length === 0 || getStatesIsLoading || getStatesError}
                        loading={getStatesIsLoading}
                    />

                    <DropDownInput
                        formik={formik}
                        fieldName={'apoderado.ciudad'}
                        label={'Ciudad'}
                        options={formatDropDownOptions(cities)}
                        isRequired={true}
                        disabled={cities?.length === 0 || getCitiesIsLoading || getCitiesError}
                        loading={getCitiesIsLoading}
                    />

                    <DropDownInput
                        formik={formik}
                        fieldName={'apoderado.municipio'}
                        label={'Municipio'}
                        options={formatDropDownOptions(municipalities)}
                        isRequired={true}
                        disabled={municipalities?.length === 0 || getMunicipalitiesIsLoading || getMunicipalitiesError}
                        loading={getMunicipalitiesIsLoading}
                    />

                    <DropDownInput
                        formik={formik}
                        fieldName={'apoderado.parroquia'}
                        label={'Parroquia'}
                        options={formatDropDownOptions(parishes)}
                        isRequired={true}
                        disabled={parishes?.length === 0 || getParishesIsLoading || getParishesError}
                        loading={getParishesIsLoading}
                    />

                    <DropDownInput
                        formik={formik}
                        fieldName={'apoderado.urbanizacion'}
                        label={'Urbanización, Sector, Barrio'}
                        options={formatDropDownOptions(sectors)}
                        isRequired={true}
                        disabled={sectors?.length === 0 || getSectorsIsLoading || getSectorsError}
                        loading={getSectorsIsLoading}
                    />

                    <TextInput
                        formik={formik}
                        fieldName={'apoderado.calle'}
                        label={'Avenida / Calle'}
                        maxLength={80}
                        isRequired={true}
                    />

                </>}

            {(formik.values?.apoderado?.pais !== '' && formik.values?.apoderado?.pais !== '0') &&
                <TextInput
                    formik={formik}
                    fieldName={"apoderado.descDireccion"}
                    label={"Dirección"}
                    maxLength={120}
                    isRequired={true}
                />}

            <TextInput
                formik={formik}
                fieldName={'apoderado.identificacionRegistro'}
                label={'Datos de contacto'}
                isRequired={true}
            />

            <MaskInput
                formik={formik}
                fieldName={'apoderado.numeroRegistro'}
                label={'Número de Registro'}
                mask={'*****'}
                isRequired={true}
            />

            <MaskInput
                formik={formik}
                fieldName={'apoderado.tomoRegistro'}
                label={'Tomo de Registro'}
                mask={'***************'}
                isRequired={true}
            />

            <MaskInput
                formik={formik}
                fieldName={'apoderado.protocolo'}
                label={'Protocolo'}
                mask={'***************'}
                isRequired={true}
            />

            <CalendarInput
                formik={formik}
                fieldName={'apoderado.fechaRegistro'}
                label={'Fecha de Registro'}
                minDate={getCurrentDateMinusYears(100)}
                maxDate={new Date()}
                isRequired={true}
            />

        </FormInputGroup>
    )
}
